import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import {
    getProductListAction,
    getFeedbackListAction,
    getInvoiceListAction,
    getQueriesAction,
    getMenusAction,
    getPagesAction,
    getThemesAction,
    getDefaultThemesAction,
    getThemeByIdAction,
    createThemeAction,
    updateThemeAction,
    upgradeThemeAction,
    getConflictsThemeAction,
    resolveConflictThemeAction,
    finishResolvingConflictsThemeAction,
    deleteThemeAction,
    getThemeTemplatesAction,
    getThemeTemplateByIdAction,
    getTemplateTypesAction,
    createThemeTemplateAction,
    updateThemeTemplateAction,
    deleteThemeTemplateAction,
    getThemeLayoutsAction,
    getThemeLayoutAction,
    createThemeLayoutAction,
    updateThemeLayoutAction,
    deleteThemeLayoutAction,
    getThemeSectionsAction,
    getThemeSectionIdAction,
    createThemeSectionAction,
    updateThemeSectionAction,
    deleteThemeSectionAction,
    getThemeBlockAction,
    createThemeBlockAction,
    updateThemeBlockAction,
    deleteThemeBlockAction,
    getThemeAssetsAction,
    getThemeAssetAction,
    createThemeAssetAction,
    updateThemeAssetAction,
    deleteThemeAssetAction,
    getThemeFilesAction,
    getThemeFileAction,
    createThemeFileAction,
    updateThemeFileAction,
    deleteThemeFileAction,
    getThemeLocaleAction,
    createThemeLocaleAction,
    updateThemeLocaleAction,
    deleteThemeLocaleAction,
    createThemeSnippetAction,
    deleteThemeSnippetAction,
    duplicateThemeAction,
    importThemeAction,
    exportThemeAction,
    duplicateJobAction,
    upgradeJobAction,
    createJobAction,
    getThemesLibraryAction,
} from "./actions";
import { toastAlert } from "@paylixecommerce/shared/components";
import { useDispatch, useSelector } from 'react-redux';

const defaultContext = {
  themes: null,
};

export const MENU_STATE = {
  main: 0,
  entitySettings: 1,
  themeSettings: 2,
  selectMenu: 3,
  selectImages: 4,
};

export const ThemesContext = createContext(defaultContext);

export const useThemesContext = () => useContext(ThemesContext);

export function ThemeProvider({ children }) {

  const dispatch = useDispatch();
  const shopId = useSelector(({ auth }) => auth?.shop?.id);

  const [defaultThemes, setDefaultThemes] = useState(null);
  const [themes, setThemes] = useState(null);
  const [loadingThemes, setLoadingThemes] = useState(null);
  const [selectedTheme, setSelectedTheme] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);

  //CODE EDITOR
  const [files, setFiles] = useState([]);
  const [templateTypes, setTemplateTypes] = useState(null);
  const [activeTabs, setActiveTabs] = useState([]);
  const [activeTabValue, setActiveTabValue] = useState(null);

  //VISUAL EDITOR
  const initialMenuState = {
    menu: MENU_STATE.main,
    entitySettings: {
      type: '',
      entity: null,
      parentKey: '',
      templateKey: '',
      sectionKey: '',
      blockKey: '',
    },
  };

  const [menuState, setMenuState] = useState(initialMenuState);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [menus, setMenus] = useState(null);
  const [pages, setPages] = useState(null);
  const [products, setProducts] = useState(null);
  const [feedbacks, setFeedbacks] = useState(null);
  const [tickets, setTickets] = useState(null);
  const [invoices, setInvoices] = useState(null);
  const [library, setLibrary] = useState(null);
  const [libraryLoading, setLibraryLoading] = useState(true);

  const openTab = (file, parent_entity, parent_name) => {
    const id = String(file.id);

    setFiles(prevFiles => [
      ...prevFiles,
      { ...file, parent_entity, parent_name },
    ]);
    setActiveTabs(prevState => [...prevState, id]);
    setActiveTabValue(id);
  };

  const resetSelectedTheme = () => {
    setSelectedTheme(null);
    setFiles([]);
    setActiveTabs([]);
    setActiveTabValue(null);
  };

  const resetMenuState = () => {
    setMenuState(initialMenuState);
  };

  // MENUS
  const getMenus = useCallback(async () => {
    const { data, error } = await dispatch(getMenusAction(shopId));

    if (error) {
      setMenus([]);
      return;
    }

    setMenus(data.menus);

    return data.menus;
  }, [dispatch, shopId]);

  const getThemesLibrary = useCallback(async () => {
      setLibraryLoading(true);
      const { data } = await dispatch(getThemesLibraryAction());
      setLibraryLoading(false);
      setLibrary(data ? data.library : []);

      return data ? data.library : [];
  }, []);

  // PAGES
  const getPages = useCallback(async () => {
    const { data } = await dispatch(getPagesAction(shopId));

    setPages(data ? data.pages : []);

    return data ? data.pages : [];
  }, [dispatch, shopId]);

  // THEMES
  const getThemes = useCallback(async () => {
    setLoadingThemes(true);
    const { data } = await dispatch(getThemesAction(shopId));
    setLoadingThemes(false);
    setThemes(data ?? []);

    return data;
  }, [dispatch, shopId]);

  const getDefaultThemes = useCallback(async () => {
    const { data } = await dispatch(getDefaultThemesAction());

    setDefaultThemes(data ? Object.values(data) : []);

    return data;
  }, [dispatch, shopId]);

  const getThemeById = useCallback(async themeId => {
      const { data, error } = await dispatch(getThemeByIdAction(shopId, themeId));

      if (error) {
        return toastAlert('error', error);
      }

      setSelectedTheme(data.theme);

      return data.theme;
    },
    [dispatch, shopId],
  );

    const terminateJob = () => {
        setJobStatus(null);
    }

    const importTheme = useCallback(async (file) => {

            setJobStatus({
                id: 999,
                description: null,
                new_theme_id: null,
                set_as_active: 0,
                substatus: null,
                status: "UPLOADING",
                type: "import"
            });

            const { data, status, error } = await dispatch(importThemeAction({ shopId, theme: file }));

            if (error) {
                if(status === 400) {
                    setJobStatus(null);
                }
                if(error) {
                    return toastAlert('error', error);
                }
                return null;
            }

            setJobStatus({
                ...data.job,
                type: "import"
            });
            createJob(data.job.id, "import");

            return true;
        }, [dispatch, shopId]
    );

    const createJob = useCallback(async (id, type) => {

        const { data, error } = await dispatch(createJobAction(id ? { id } : jobStatus));

        if (error) {
            return toastAlert('error', error);
        }

        if(data.job.status === "COMPLETED" || data.job.status === "ABORTED") {
            getThemes();
        }

        setJobStatus({
            ...data.job,
            type: type || "clone"
        });

        return data;
    }, [jobStatus]);

    const createTheme = useCallback(async (id, isCreate) => {

      const { data, error } = await dispatch(createThemeAction({ shopId, id: id ? id : 'default' }));

      if (error) {
        return toastAlert('error', error);
      }

        setJobStatus({
            ...data.job,
            type: isCreate ? "create" : "clone"
        });
        createJob(data.job.id, isCreate ? "create" : "clone");

      return data;
    }, [dispatch, shopId]);


    const duplicateJob = useCallback(async (id) => {

        const { data, error } = await dispatch(duplicateJobAction(id ? { id } : jobStatus));

        if (error) {
            return toastAlert('error', error);
        }

        if(data.job.status === "COMPLETED" || data.job.status === "ABORTED") {
            getThemes();
        }

        setJobStatus({
            ...data.job,
            type: "duplicate"
        });

        return data;

    }, [jobStatus]);

    const duplicateTheme = useCallback(async id => {

      const { data, error } = await dispatch(duplicateThemeAction({ shopId, id }));

      if (error) {
        return toastAlert('error', error);
      }

      setJobStatus({
          ...data.job,
          type: "duplicate"
      });
      duplicateJob(data.job.id);

      return data;
    }, [dispatch, shopId]);


    const upgradeJob = useCallback(async (id) => {

        const { data, error } = await dispatch(upgradeJobAction(id ? { id } : jobStatus));

        if (error) {
            return toastAlert('error', error);
        }

        if(data.job.status === "COMPLETED" || data.job.status === "ABORTED") {
            getThemes();
        }

        setJobStatus({
            ...data.job,
            type: "update"
        });

        return data;

    }, [jobStatus, dispatch]);

    const upgradeTheme = useCallback(async (theme, merge_type = "", doesNotice = true) => {

      const { data, error } = await dispatch(upgradeThemeAction({ shopId, themeId: theme.id, merge_type }),);

      if (error) {
        return toastAlert('error', error);
      }

      if (data.conflicts) {
        return data;
      }

      setJobStatus({
          ...data.job,
          type: "update"
      });
      upgradeJob(data.job.id);

      return data;

    }, [dispatch, shopId]);

    const updateTheme = useCallback(async (theme, name, isActive, doesNotice = true) => {
        const body = {
            name,
            is_active: isActive,
            settings: JSON.stringify(theme.settings)
        };

        const { data, error } = await dispatch(
            updateThemeAction({ shopId, themeId: theme.id, ...body }),
        );

        if (error) {
            return toastAlert('error', error);
        }

        setThemes(prevThemes => {
            const themes = isActive
                ? prevThemes.map(theme => ({ ...theme, is_active: 0 }))
                : prevThemes;
            return themes.map(prevTheme =>
                prevTheme.id === data.theme.id ? data.theme : prevTheme,
            );
        });

        doesNotice && toastAlert('success', 'Success');

        return data.theme;

    }, [dispatch, shopId]);


    const getConflicts = useCallback(async (theme, doesNotice = true) => {
      const { data, error } = await dispatch(
        getConflictsThemeAction({ shopId, themeId: theme.id }),
      );

      if (error) {
        return toastAlert('error', error);
      }
      return data;
    }, [dispatch, shopId]);

  const resolveConflict = useCallback(async (theme, conflictId, merge_result, doesNotice = true) => {
      const { data, error } = await dispatch(
        resolveConflictThemeAction({ shopId, conflictId, themeId: theme.id, merge_result }),
      );

      if (error) {
        return toastAlert('error', error);
      }
      return data;
    }, [dispatch, shopId]);

  const finishResolvingConflicts = useCallback(async (theme, doesNotice = true) => {
      const { data, error } = await dispatch(
        finishResolvingConflictsThemeAction({ shopId, themeId: theme.id }),
      );

      if (error) {
        return toastAlert('error', error);
      }
      return data;
    }, [dispatch, shopId]);

  const deleteTheme = useCallback(async themeId => {

      const { error } = await dispatch(deleteThemeAction({ shopId, themeId }));

      if (error) {
        return toastAlert('error', error);
      }

      setThemes(prevThemes => prevThemes.filter(({ id }) => id !== themeId));

      toastAlert('success', 'Success');

      return true;
    }, [dispatch, shopId]
  );

  const exportTheme = useCallback(async themeId => {
      const res = await dispatch(exportThemeAction({ shopId, themeId }));

      if (res?.error) {
        return toastAlert('error', res?.error);
      }
      toastAlert('success', 'Success');

      return true;
    }, [dispatch, shopId]
  );



  // FILES
  const getThemeFiles = useCallback(async themeId => {
      const { data } = await dispatch(getThemeFilesAction({ shopId, themeId }));

      setFiles(data ? data.files : []);

      return data ? data.files : [];
    }, [dispatch, shopId]
  );

  const getThemeFileById = useCallback(({ themeId, fileId }) => dispatch(getThemeFileAction({ shopId, themeId, fileId })), [dispatch, shopId]);

  const createThemeFile = useCallback(async (themeId, file) => {
      const { data, error } = await dispatch(createThemeFileAction({ shopId, themeId, file }));

      if (error) {
        return toastAlert('error', error);
      }

      return data.file;
    },
    [dispatch, shopId],
  );

  const updateThemeFile = useCallback(async (themeId, fileId, file, parent_entity, parent_name) => {
      const { data, error } = await dispatch(updateThemeFileAction({ shopId, themeId, fileId, file }));

      if (error) {
        return toastAlert('error', error);
      }

      setFiles(prevFiles => [
        ...prevFiles.map(prevFile =>
          prevFile.id === data.file.id
            ? { ...data.file, parent_entity, parent_name }
            : prevFile,
        ),
      ]);
      toastAlert('success', 'Success');

      return data.file;
    }, [dispatch, shopId]
  );

  const deleteThemeFile = useCallback(
    (themeId, fileId) => {
      return dispatch(deleteThemeFileAction({ shopId, themeId, fileId }));
    }, [dispatch, shopId]);

  // LAYOUTS
  const getThemeLayouts = useCallback(themeId => {
      return dispatch(getThemeLayoutsAction({ shopId, themeId }));
    }, [dispatch, shopId]
  );

  const getThemeLayoutById = useCallback(({ themeId, layoutId }) => {
      return dispatch(getThemeLayoutAction({ shopId, themeId, layoutId }));
    }, [dispatch, shopId]
  );

  const createThemeLayout = useCallback(async (themeId, name, isDefault, file, settings) => {

      const fileResponse = await createThemeFile(themeId, file);

      const body = {
        file_id: fileResponse.id,
        settings,
        name,
        is_default: isDefault,
      };

      const { data, error } = await dispatch(
        createThemeLayoutAction({ shopId, themeId, ...body }),
      );

      if (error) {
        return toastAlert('error', error);
      }

      setSelectedTheme(prevTheme => {
        const layouts = prevTheme.layouts;

        if (!!data.layout.is_default) {
          const prevDefaultLayout = Object.values(layouts).find(
            ({ is_default }) => is_default,
          );

          if (prevDefaultLayout) {
            layouts[prevDefaultLayout.name].is_default = 0;
          }
        }

        return {
          ...prevTheme,
          layouts: {
            ...prevTheme.layouts,
            [data.layout.name]: {
              ...data.layout,
              settings: JSON.parse(settings),
              file: fileResponse,
            },
          },
        };
      });

      openTab(fileResponse, 'layout', data.layout.name);
      toastAlert('success', 'Success');

      return data.layout;
    },
    [dispatch, shopId],
  );

  const updateThemeLayout = useCallback(async (themeId, layoutId, isDefault, name, item, doesNotice = true) => {

      const body = {
        file_id: item.file.id,
        settings: JSON.stringify(item.settings),
        name,
        is_default: isDefault,
      };

      const { data, error } = await dispatch(
        updateThemeLayoutAction({ shopId, themeId, layoutId, ...body }),
      );

      if (error) {
        return toastAlert('error', error);
      }

      setSelectedTheme(prevTheme => {
        const layouts = prevTheme.layouts;
        delete layouts[item.name];

        if (!!data.layout.is_default) {
          const prevDefaultLayout = Object.values(layouts).find(
            ({ is_default }) => is_default,
          );

          if (prevDefaultLayout) {
            layouts[prevDefaultLayout.name].is_default = 0;
          }
        }

        return {
          ...prevTheme,
          layouts: {
            ...prevTheme.layouts,
            [data.layout.name]: { ...data.layout, file: item.file },
          },
        };
      });

      setFiles(prevFiles =>
        prevFiles.map(prevFile =>
          prevFile.parent_name === item.name &&
          prevFile.parent_entity === 'layout'
            ? { ...prevFile, parent_name: data.layout.name }
            : prevFile,
        ),
      );

      doesNotice && toastAlert('success', 'Success');

      return data.layout;
    }, [dispatch, shopId]
  );

  const deleteThemeLayout = useCallback(async (themeId, layout) => {

      const { error } = await dispatch(deleteThemeLayoutAction({ shopId, themeId, layoutId: layout.id }));

      if (error) {
        return toastAlert('error', error);
      }

      setSelectedTheme(prevTheme => {
        const layouts = prevTheme.layouts;
        delete layouts[layout.name];
        return {
          ...prevTheme,
          layouts,
        };
      });

      toastAlert('success', 'Success');

      return true;
    },
    [dispatch, shopId],
  );

  // TEMPLATES
  const getThemeTemplates = useCallback(themeId => {
      return dispatch(getThemeTemplatesAction({ shopId, themeId }));
    }, [dispatch, shopId]
  );

  const getThemeTemplateById = useCallback(({ themeId, templateId }) => {
      return dispatch(getThemeTemplateByIdAction({ shopId, themeId, templateId }));
    }, [dispatch, shopId]
  );

  const getThemeTemplateTypes = useCallback(async () => {

    const { data, error } = await dispatch(getTemplateTypesAction(shopId));

    if (error) {
      return toastAlert('error', error);
    }

    setTemplateTypes(data.types);

    return data.types;
  }, [dispatch, shopId]);

  const createThemeTemplate = useCallback(
    async (themeId, name, templateType, isDefault, file, settings) => {
      const fileResponse = await createThemeFile(themeId, file);

      const body = {
        file_id: fileResponse.id,
        settings,
        name,
        is_default: isDefault,
        template_type: templateType,
      };

      const { data, error } = await dispatch(createThemeTemplateAction({ shopId, themeId, ...body }));

      if (error) {
        return toastAlert('error', error);
      }

      setSelectedTheme(prevTheme => {
        const templates = prevTheme.templates;

        if (!!data.template.is_default) {
          const prevDefaultTemplate = Object.values(templates).find(
            ({ is_default, template_type }) =>
              is_default && template_type === data.template.template_type,
          );

          if (prevDefaultTemplate) {
            templates[prevDefaultTemplate.name].is_default = 0;
          }
        }

        return {
          ...prevTheme,
          templates: {
            ...prevTheme.templates,
            [data.template.name]: {
              ...data.template,
              settings: JSON.parse(settings),
              file: fileResponse,
            },
          },
        };
      });

      openTab(fileResponse, 'template', data.template.name);
      toastAlert('success', 'Success');

      return data.template;
    },
    [dispatch, shopId],
  );

  const updateThemeTemplate = useCallback(
    async (themeId, templateId, name, isDefault, item, doesNotice = true) => {
      const body = {
        file_id: item.file.id,
        settings: JSON.stringify(item.settings),
        name,
        is_default: isDefault,
        template_type: item.template_type,
      };

      const { data, error } = await dispatch(
        updateThemeTemplateAction({ shopId, themeId, templateId, ...body }),
      );

      if (error) {
        return toastAlert('error', error);
      }

      setSelectedTheme(prevTheme => {
        const templates = prevTheme.templates;
        delete templates[item.name];

        if (!!data.template.is_default) {
          const prevDefaultTemplate = Object.values(templates).find(
            ({ is_default, template_type }) =>
              is_default && template_type === data.template.template_type,
          );

          if (prevDefaultTemplate) {
            templates[prevDefaultTemplate.name].is_default = 0;
          }
        }

        return {
          ...prevTheme,
          templates: {
            ...prevTheme.templates,
            [data.template.name]: { ...data.template, file: item.file },
          },
        };
      });

      setFiles(prevFiles =>
        prevFiles.map(prevFile =>
          prevFile.parent_name === item.name &&
          prevFile.parent_entity === 'template'
            ? { ...prevFile, parent_name: data.template.name }
            : prevFile,
        ),
      );

      doesNotice && toastAlert('success', 'Success');

      return data.template;
    },
    [dispatch, shopId],
  );

  const deleteThemeTemplate = useCallback(
    async (themeId, template) => {
      const { error } = await dispatch(
        deleteThemeTemplateAction({ shopId, themeId, templateId: template.id }),
      );

      if (error) {
        return toastAlert('error', error);
      }

      setSelectedTheme(prevTheme => {
        const templates = prevTheme.templates;
        delete templates[template.name];
        return {
          ...prevTheme,
          templates,
        };
      });

      toastAlert('success', 'Success');

      return true;
    },
    [dispatch, shopId],
  );

  // SECTIONS
  const getThemeSections = useCallback(
    themeId => {
      return dispatch(getThemeSectionsAction({ shopId, themeId }));
    },
    [dispatch, shopId],
  );

  const getThemeSectionById = useCallback(
    ({ themeId, sectionId }) => {
      return dispatch(getThemeSectionIdAction({ shopId, themeId, sectionId }));
    },
    [dispatch, shopId],
  );

  const createThemeSection = useCallback(
    async (themeId, name, file, settings) => {
      const fileResponse = await createThemeFile(themeId, file);

      const body = {
        file_id: fileResponse.id,
        settings,
        name,
      };

      const { data, error } = await dispatch(
        createThemeSectionAction({ shopId, themeId, ...body }),
      );

      if (error) {
        return toastAlert('error', error);
      }

      setSelectedTheme(prevTheme => ({
        ...prevTheme,
        sections: {
          ...prevTheme.sections,
          [data.section.name]: {
            ...data.section,
            settings: JSON.parse(settings),
            file: fileResponse,
          },
        },
      }));

      openTab(fileResponse, 'section', data.section.name);
      toastAlert('success', 'Success');

      return data.section;
    },
    [dispatch, shopId],
  );

  const updateThemeSection = useCallback(
    async (themeId, sectionId, name, item) => {
      const body = {
        file_id: item.file.id,
        settings: JSON.stringify(item.settings),
        name,
      };

      const { data, error } = await dispatch(
        updateThemeSectionAction({ shopId, themeId, sectionId, ...body }),
      );

      if (error) {
        return toastAlert('error', error);
      }

      setSelectedTheme(prevTheme => {
        const sections = prevTheme.sections;
        delete sections[item.name];
        return {
          ...prevTheme,
          sections: {
            ...prevTheme.sections,
            [data.section.name]: { ...data.section, file: item.file },
          },
        };
      });

      setFiles(prevFiles =>
        prevFiles.map(prevFile =>
          prevFile.parent_name === item.name &&
          prevFile.parent_entity === 'section'
            ? { ...prevFile, parent_name: data.section.name }
            : prevFile,
        ),
      );

      toastAlert('success', 'Success');

      return data.section;
    },
    [dispatch, shopId],
  );

  const deleteThemeSection = useCallback(
    async (themeId, section) => {
      const { error } = await dispatch(
        deleteThemeSectionAction({ shopId, themeId, sectionId: section.id }),
      );

      if (error) {
        return toastAlert('error', error);
      }

      setSelectedTheme(prevTheme => {
        const sections = prevTheme.sections;
        delete sections[section.name];
        return {
          ...prevTheme,
          sections,
        };
      });

      toastAlert('success', 'Success');

      return true;
    },
    [dispatch, shopId],
  );

  // BLOCKS
  const getThemeBlocks = useCallback(
    themeId => {
      return dispatch(getThemeBlockAction({ shopId, themeId }));
    },
    [dispatch, shopId],
  );

  const getThemeBlockById = useCallback(
    ({ themeId, blockId }) => {
      return dispatch(getThemeBlockAction({ shopId, themeId, blockId }));
    },
    [dispatch, shopId],
  );

  const createThemeBlock = useCallback(
    async (themeId, name, file, settings) => {
      const fileResponse = await createThemeFile(themeId, file);

      const body = {
        file_id: fileResponse.id,
        settings,
        name,
      };

      const { data, error } = await dispatch(
        createThemeBlockAction({ shopId, themeId, ...body }),
      );

      if (error) {
        return toastAlert('error', error);
      }

      setSelectedTheme(prevTheme => ({
        ...prevTheme,
        blocks: {
          ...prevTheme.blocks,
          [data.block.name]: {
            ...data.block,
            settings: JSON.parse(settings),
            file: fileResponse,
          },
        },
      }));

      openTab(fileResponse, 'block', data.block.name);
      toastAlert('success', 'Success');

      return data.block;
    },
    [dispatch, shopId],
  );

  const updateThemeBlock = useCallback(
    async (themeId, blockId, name, item) => {
      const body = {
        file_id: item.file.id,
        settings: JSON.stringify(item.settings),
        name,
      };

      const { data, error } = await dispatch(
        updateThemeBlockAction({ shopId, themeId, blockId, ...body }),
      );

      if (error) {
        return toastAlert('error', error);
      }

      setSelectedTheme(prevTheme => {
        const blocks = prevTheme.blocks;
        delete blocks[item.name];
        return {
          ...prevTheme,
          blocks: {
            ...prevTheme.blocks,
            [data.block.name]: { ...data.block, file: item.file },
          },
        };
      });

      setFiles(prevFiles =>
        prevFiles.map(prevFile =>
          prevFile.parent_name === item.name &&
          prevFile.parent_entity === 'block'
            ? { ...prevFile, parent_name: data.block.name }
            : prevFile,
        ),
      );

      toastAlert('success', 'Success');

      return data.block;
    },
    [dispatch, shopId],
  );

  const deleteThemeBlock = useCallback(
    async (themeId, block) => {
      const { error } = await dispatch(
        deleteThemeBlockAction({ shopId, themeId, blockId: block.id }),
      );

      if (error) {
        return toastAlert('error', error);
      }

      setSelectedTheme(prevTheme => {
        const blocks = prevTheme.blocks;
        delete blocks[block.name];
        return {
          ...prevTheme,
          blocks,
        };
      });

      toastAlert('success', 'Success');

      return true;
    },
    [dispatch, shopId],
  );

  // ASSETS
  const getThemeAssets = useCallback(
    async themeId => {
      const { data } = await dispatch(
        getThemeAssetsAction({ shopId, themeId }),
      );

      return data.assets;
    },
    [dispatch, shopId],
  );

  const getThemeAssetById = useCallback(
    ({ themeId, assetId }) => {
      return dispatch(getThemeAssetAction({ shopId, themeId, assetId }));
    },
    [dispatch, shopId],
  );

  const createThemeAsset = useCallback(
    async (themeId, file) => {
      const fileResponse = await createThemeFile(themeId, file);

      if (!fileResponse) {
        return;
      }

      const body = {
        file_id: fileResponse.id,
        name: fileResponse.name,
      };

      const { data, error } = await dispatch(
        createThemeAssetAction({ shopId, themeId, ...body }),
      );

      if (error) {
        return toastAlert('error', error);
      }

      setSelectedTheme(prevTheme => ({
        ...prevTheme,
        assets: {
          ...prevTheme.assets,
          [data.asset.name]: { ...data.asset, file: fileResponse },
        },
      }));

      openTab(fileResponse, 'asset', data.asset.name);
      toastAlert('success', 'Success');

      return data.asset;
    },
    [dispatch, shopId],
  );

  const updateThemeAsset = useCallback(
    (themeId, assetId, params) => {
      return dispatch(
        updateThemeAssetAction({ shopId, themeId, assetId, ...params }),
      );
    },
    [dispatch, shopId],
  );

  const deleteThemeAsset = useCallback(
    async (themeId, asset) => {
      const { error } = dispatch(
        deleteThemeAssetAction({ shopId, themeId, assetId: asset.id }),
      );

      if (error) {
        return toastAlert('error', error);
      }

      setSelectedTheme(prevTheme => {
        const assets = prevTheme.assets;
        delete assets[asset.name];
        return {
          ...prevTheme,
          assets,
        };
      });

      toastAlert('success', 'Success');

      return true;
    },
    [dispatch, shopId],
  );

  // LOCALES
  const getThemeLocales = useCallback(
    themeId => {
      return dispatch(getThemeLocaleAction({ shopId, themeId }));
    },
    [dispatch, shopId],
  );

  const getThemeLocaleById = useCallback(
    ({ themeId, localeId }) => {
      return dispatch(getThemeLocaleAction({ shopId, themeId, localeId }));
    },
    [dispatch, shopId],
  );

  const createThemeLocale = useCallback(
    async (themeId, name, file) => {
      const fileResponse = await createThemeFile(themeId, file);

      const body = {
        file_id: fileResponse.id,
        name,
      };

      const { data, error } = await dispatch(
        createThemeLocaleAction({ shopId, themeId, ...body }),
      );

      if (error) {
        return toastAlert('error', error);
      }

      setSelectedTheme(prevTheme => ({
        ...prevTheme,
        locales: {
          ...prevTheme.locales,
          [data.locale.name]: { ...data.locale, file: fileResponse },
        },
      }));

      openTab(fileResponse, 'locale', data.locale.name);
      toastAlert('success', 'Success');

      return data.locale;
    },
    [dispatch, shopId],
  );

  const updateThemeLocale = useCallback(
    (themeId, localeId, params) => {
      return dispatch(
        updateThemeLocaleAction({ shopId, themeId, localeId, ...params }),
      );
    },
    [dispatch, shopId],
  );

  const deleteThemeLocale = useCallback(
    async (themeId, locale) => {
      const { error } = dispatch(
        deleteThemeLocaleAction({ shopId, themeId, localeId: locale.id }),
      );

      if (error) {
        return toastAlert('error', error);
      }

      setSelectedTheme(prevTheme => {
        const locales = prevTheme.locales;
        delete locales[locale.name];
        return {
          ...prevTheme,
          locales,
        };
      });

      toastAlert('success', 'Success');

      return true;
    },
    [dispatch, shopId],
  );

  // SNIPPETS
  const createThemeSnippet = useCallback(
    async (themeId, name, file) => {
      const fileResponse = await createThemeFile(themeId, file);

      const body = {
        file_id: fileResponse.id,
        name,
      };

      const { data, error } = await dispatch(
        createThemeSnippetAction({ shopId, themeId, ...body }),
      );

      if (error) {
        return toastAlert('error', error);
      }

      setSelectedTheme(prevTheme => ({
        ...prevTheme,
        snippets: {
          ...prevTheme.snippets,
          [data.snippet.name]: { ...data.snippet, file: fileResponse },
        },
      }));

      openTab(fileResponse, 'snippet', data.snippet.name);
      toastAlert('success', 'Success');

      return data.snippet;
    },
    [dispatch, shopId],
  );

  const deleteThemeSnippet = useCallback(
    async (themeId, snippet) => {
      const { error } = dispatch(
        deleteThemeSnippetAction({ shopId, themeId, snippetId: snippet.id }),
      );

      if (error) {
        return toastAlert('error', error);
      }

      setSelectedTheme(prevTheme => {
        const snippets = prevTheme.snippets;
        delete snippets[snippet.name];
        return {
          ...prevTheme,
          snippets,
        };
      });

      toastAlert('success', 'Success');

      return true;
    },
    [dispatch, shopId],
  );

  // IMAGES
  const uploadImages = useCallback(
    async (themeId, images) => {
      const uploadedFiles = await Promise.all(
        images.map(async image => {
          const fileResponse = await createThemeFile(themeId, image);

          const body = {
            file_id: fileResponse.id,
            name: fileResponse.name,
          };

          const { data, error } = await dispatch(
            createThemeAssetAction({ shopId, themeId, ...body }),
          );

          if (error) {
            return toastAlert('error', error);
          }

          setSelectedTheme(prevTheme => ({
            ...prevTheme,
            assets: {
              ...prevTheme.assets,
              [data.asset.name]: { ...data.asset, file: fileResponse },
            },
          }));

          return fileResponse;
        }),
      );

      return uploadedFiles.filter((file) => typeof file === 'object');
    },
    [dispatch, shopId],
  );

  //PRODUCTS
  const getProductList = useCallback(async () => {
    const { data, status } = await dispatch(getProductListAction());

    if (status === 200 && data.products.length) {
      setProducts(data?.products);
    }
  }, [dispatch, shopId]);

  //FEEDBACKS
  const getFeedbackList = useCallback(async () => {
    const { data, status } = await dispatch(getFeedbackListAction());

    if (status === 200 && data.feedback.length) {
      setFeedbacks(data?.feedback);
    }
  }, [dispatch, shopId]);

  //TICKETS
  const getTicketList = useCallback(async () => {
    const { data, status } = await dispatch(getQueriesAction());

    if (status === 200 && data.queries.length) {
      setTickets(data?.queries);
    }
  }, [dispatch, shopId]);

  //INVOICES
  const getInvoiceList = useCallback(async () => {
    const { data, status } = await dispatch(getInvoiceListAction());

    if (status === 200 && data.invoices.length) {
      setInvoices(data?.invoices);
    }
  }, [dispatch, shopId]);

  useEffect(() => {
    getThemes();
    getDefaultThemes();
    getThemesLibrary()
  }, [getThemes]);

  return (
    <ThemesContext.Provider
      value={{
        themes,
        loadingThemes,
        defaultThemes,
        selectedTheme,
        templateTypes,
        files,
        activeTabs,
        activeTabValue,
        setActiveTabs,
        setActiveTabValue,
        setFiles,
        openTab,
        resetSelectedTheme,
        getThemes,
        getThemeById,
        getThemeFiles,
        updateThemeFile,
        updateTheme,

        getConflicts,
        resolveConflict,
        finishResolvingConflicts,

          createTheme,
          createJob,
          duplicateTheme,
          duplicateJob,
          upgradeTheme,
          upgradeJob,
          jobStatus,
          terminateJob,

        deleteTheme,
        importTheme,
        exportTheme,
        createThemeLayout,
        updateThemeLayout,
        deleteThemeLayout,
        getThemeTemplateTypes,
        createThemeTemplate,
        updateThemeTemplate,
        deleteThemeTemplate,
        createThemeSection,
        updateThemeSection,
        deleteThemeSection,
        createThemeBlock,
        updateThemeBlock,
        deleteThemeBlock,
        createThemeAsset,
        deleteThemeAsset,
        createThemeLocale,
        deleteThemeLocale,
        createThemeSnippet,
        deleteThemeSnippet,

          setLoadingThemes,
          libraryLoading,

        //VISUAL EDITOR
        menuState,
        selectedTemplate,
        menus,
        pages,
        products,
        feedbacks,
        tickets,
        invoices,
        setMenuState,
        resetMenuState,
        setSelectedTemplate,
        getMenus,
        getPages,
        getProductList,
        getFeedbackList,
        getTicketList,
        getInvoiceList,
        uploadImages,
        getThemesLibrary,
        library,
      }}
    >
      {children}
    </ThemesContext.Provider>
  );
}
